import React, { useState } from 'react';
import AppointmentEditDialog from './AppointmentEditDialog';
import UnavailableBlockEditDialog from './UnavailableBlockEditDialog';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Tab,
  Tabs
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import BlockIcon from '@mui/icons-material/Block';
import { CalendarEvent, UnavailableBlock } from '../../models/calendar';

interface AppointmentDetailProps {
  selectedDate: Date | null;
  events: CalendarEvent[];
  practitioners: any[];
  appointmentTypes: any[];
  onAppointmentUpdated: () => void;
}

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AppointmentDetail: React.FC<AppointmentDetailProps> = ({ 
  selectedDate, 
  events,
  practitioners,
  appointmentTypes,
  onAppointmentUpdated
}) => {
  const [selectedAppointment, setSelectedAppointment] = useState<CalendarEvent | null>(null);
  const [selectedUnavailableBlock, setSelectedUnavailableBlock] = useState<UnavailableBlock | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editBlockDialogOpen, setEditBlockDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Format date as a string (e.g., "Monday, 1 January 2023")
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  // Format time (e.g., "09:30 AM")
  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  // Calculate duration in minutes between two dates
  const calculateDuration = (start: Date, end: Date) => {
    return Math.round((end.getTime() - start.getTime()) / 60000);
  };

  // Filter events for the selected date
  const filterEventsForDay = (events: CalendarEvent[], type: 'appointment' | 'unavailable') => {
    return events.filter(event => 
      event.type === type && 
      (type === 'appointment' ? !event.cancelled : true) && // Exclude cancelled appointments
      selectedDate && 
      event.start.getDate() === selectedDate.getDate() &&
      event.start.getMonth() === selectedDate.getMonth() &&
      event.start.getFullYear() === selectedDate.getFullYear()
    ).sort((a, b) => a.start.getTime() - b.start.getTime());
  };

  // Get appointments and unavailable blocks for the selected date
  const appointmentsForDay = filterEventsForDay(events, 'appointment');
  const unavailableBlocksForDay = filterEventsForDay(events, 'unavailable');

  // Convert CalendarEvent to UnavailableBlock
  const calendarEventToUnavailableBlock = (event: CalendarEvent): UnavailableBlock => {
    return {
      id: event.id,
      practitioner_id: event.practitionerId,
      practitioner_name: event.practitionerName,
      starts_at: event.start.toISOString(),
      ends_at: event.end.toISOString(),
      notes: event.notes,
      unavailable_block_type_id: undefined
    };
  };

  if (!selectedDate) {
    return (
      <Paper sx={{ 
        p: 3, 
        mt: 2, 
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '150px',
        bgcolor: 'background.default'
      }}>
        <Typography 
          variant="subtitle1" 
          sx={{ color: 'text.secondary', fontStyle: 'italic' }}
        >
          Please select a date to view appointments
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper 
        sx={{ 
          p: 3, 
          mt: 2, 
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
      >
        <Typography 
          variant="h6" 
          gutterBottom
          sx={{ 
            color: 'primary.dark',
            pb: 1,
            borderBottom: '2px solid',
            borderColor: 'primary.light',
            fontWeight: 'bold',
            display: 'inline-block'
          }}
        >
          Schedule for {formatDate(selectedDate)}
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="schedule tabs"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon fontSize="small" />
                  <span>Appointments ({appointmentsForDay.length})</span>
                </Box>
              } 
              id="tab-0" 
              aria-controls="tabpanel-0" 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BlockIcon fontSize="small" />
                  <span>Unavailable Blocks ({unavailableBlocksForDay.length})</span>
                </Box>
              } 
              id="tab-1" 
              aria-controls="tabpanel-1" 
            />
          </Tabs>
        </Box>

        {/* Appointments Tab */}
        <TabPanel value={tabValue} index={0}>
          {appointmentsForDay.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 4, 
              minHeight: '150px',
              bgcolor: 'background.default',
              borderRadius: 1,
            }}>
              <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No appointments scheduled for this day
              </Typography>
            </Box>
          ) : (
            <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
              {appointmentsForDay.map((appointment, index) => (
                <React.Fragment key={appointment.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem 
                    alignItems="flex-start" 
                    sx={{ 
                      '&:hover': { 
                        bgcolor: 'rgba(0, 0, 0, 0.03)' 
                      },
                      transition: 'background-color 0.2s',
                      py: 1.5,
                      cursor: 'pointer',
                      '&.highlight-appointment': {
                        animation: 'highlight-pulse 1.5s ease-in-out',
                        backgroundColor: 'rgba(25, 118, 210, 0.15)',
                      },
                      '@keyframes highlight-pulse': {
                        '0%': { backgroundColor: 'rgba(25, 118, 210, 0.3)' },
                        '50%': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                        '100%': { backgroundColor: 'transparent' }
                      }
                    }}
                    data-appointment-id={appointment.id}
                    onClick={() => {
                      setSelectedAppointment(appointment);
                      setEditDialogOpen(true);
                    }}
                  >
                    <ListItemIcon>
                      <AccessTimeIcon color="primary" sx={{ fontSize: 28 }} />
                    </ListItemIcon>
                    <ListItemSecondaryAction>
                      <Tooltip title="Edit Appointment">
                        <IconButton 
                          edge="end" 
                          aria-label="edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAppointment(appointment);
                            setEditDialogOpen(true);
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} component="span">
                          <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                            {formatTime(appointment.start)}
                          </Box>
                          <Chip 
                            label={`${calculateDuration(appointment.start, appointment.end)} min`} 
                            size="small" 
                            color="primary" 
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box component="div">
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }} component="div">
                            <PersonIcon sx={{ fontSize: 20, mr: 1, color: 'primary.light' }} />
                            <span>{appointment.patientName || 'Unknown Patient'}</span>
                          </Box>
                          {appointment.patientPhone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, pl: 3 }} component="div">
                              <span>Ph: {appointment.patientPhone}</span>
                            </Box>
                          )}
                          {appointment.patientEmail && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, pl: 3 }} component="div">
                              <span style={{ 
                                wordBreak: 'break-word',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                Email: {appointment.patientEmail}
                              </span>
                            </Box>
                          )}
                          {appointment.practitionerName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }} component="div">
                              <EventIcon sx={{ fontSize: 20, mr: 1, color: 'primary.light' }} />
                              <span>{appointment.practitionerName}</span>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>

        {/* Unavailable Blocks Tab */}
        <TabPanel value={tabValue} index={1}>
          {unavailableBlocksForDay.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 4, 
              minHeight: '150px',
              bgcolor: 'background.default',
              borderRadius: 1,
            }}>
              <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No unavailable blocks for this day
              </Typography>
            </Box>
          ) : (
            <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
              {unavailableBlocksForDay.map((block, index) => (
                <React.Fragment key={block.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem 
                    alignItems="flex-start" 
                    sx={{ 
                      '&:hover': { 
                        bgcolor: 'rgba(244, 67, 54, 0.05)' 
                      },
                      transition: 'background-color 0.2s',
                      py: 1.5,
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setSelectedUnavailableBlock(calendarEventToUnavailableBlock(block));
                      setEditBlockDialogOpen(true);
                    }}
                  >
                    <ListItemIcon>
                      <BlockIcon color="error" sx={{ fontSize: 28 }} />
                    </ListItemIcon>
                    <ListItemSecondaryAction>
                      <Tooltip title="Edit Unavailable Block">
                        <IconButton 
                          edge="end" 
                          aria-label="edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUnavailableBlock(calendarEventToUnavailableBlock(block));
                            setEditBlockDialogOpen(true);
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} component="span">
                          <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                            {formatTime(block.start)} - {formatTime(block.end)}
                          </Box>
                          <Chip 
                            label={`${calculateDuration(block.start, block.end)} min`} 
                            size="small" 
                            color="error" 
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box component="div">
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }} component="div">
                            <Typography variant="body2" color="text.secondary">
                              {block.title}
                            </Typography>
                          </Box>
                          {block.notes && (
                            <Box sx={{ mt: 0.5 }} component="div">
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {block.notes}
                              </Typography>
                            </Box>
                          )}
                          {block.practitionerName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }} component="div">
                              <EventIcon sx={{ fontSize: 20, mr: 1, color: 'error.light' }} />
                              <span>{block.practitionerName}</span>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>
      </Paper>
      
      {/* Appointment Edit Dialog */}
      <AppointmentEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        appointment={selectedAppointment}
        practitioners={practitioners}
        appointmentTypes={appointmentTypes}
        onAppointmentUpdated={onAppointmentUpdated}
      />

      {/* Unavailable Block Edit Dialog */}
      <UnavailableBlockEditDialog
        open={editBlockDialogOpen}
        onClose={() => setEditBlockDialogOpen(false)}
        block={selectedUnavailableBlock}
        practitioners={practitioners}
        onBlockUpdated={onAppointmentUpdated}
        onBlockDeleted={onAppointmentUpdated}
      />
    </>
  );
};

export default AppointmentDetail;